/**
 * Sticky add-to-cart bar for the product page.
 *
 * Design notes:
 * - The bar owns no variant state. Its dropdowns write into the main variant picker
 *   and dispatch `change` there, so VariantSelects/VariantRadios in global.js remains
 *   the single source of truth for the selected variant.
 * - On `variantChange` the theme has already fetched a fresh render of the whole
 *   main-product section and hands it over on the event. The bar reads its own
 *   updated price / label / image out of that document rather than re-fetching or
 *   formatting money in JS.
 * - The Klaviyo back-in-stock node is never re-rendered here (see the note in
 *   snippets/sticky-buy-buttons.liquid).
 */
if (!customElements.get('sticky-buy-buttons')) {
  customElements.define(
    'sticky-buy-buttons',
    class StickyBuyButtons extends HTMLElement {
      connectedCallback() {
        // Relocating the element to <body> re-triggers this callback.
        if (this.initialized) return;
        this.initialized = true;

        this.sectionId = this.dataset.section;

        this.mainPicker = document.querySelector(
          `variant-selects[data-section="${this.sectionId}"], variant-radios[data-section="${this.sectionId}"]`
        );
        this.submitButton = this.querySelector('.sticky-buy-buttons__submit');
        this.chooseButton = this.querySelector('[data-sticky-choose-options]');
        this.spinner = this.querySelector('.loading-overlay__spinner');

        // Because the bar gets relocated out of the section wrapper, a theme editor
        // re-render leaves the previous copy behind in <body>. Clear it out first.
        document.querySelectorAll(`sticky-buy-buttons#${CSS.escape(this.id)}`).forEach((bar) => {
          if (bar !== this) bar.remove();
        });

        // A `position: fixed` element is positioned against the nearest transformed
        // ancestor, and the product info column can pick up a transform from the
        // scroll-reveal animation. Moving the bar to <body> makes that impossible.
        if (this.parentElement !== document.body) document.body.appendChild(this);

        this.scrolledPastAnchor = false;
        this.footerInView = false;

        this.bindEvents();
        this.updateHeaderOffset();
        this.observeAnchor();
        this.observeFooter();
      }

      disconnectedCallback() {
        // Leave no lift behind for a bar that no longer exists (theme editor re-render).
        document.documentElement.classList.remove('sticky-buy-buttons-lifting-widgets');
        document.documentElement.style.setProperty('--sticky-buy-buttons-height', '0px');

        this.observer?.disconnect();
        this.footerObserver?.disconnect();
        this.headerClassObserver?.disconnect();
        this.headerResizeObserver?.disconnect();
        this.selfResizeObserver?.disconnect();
        if (this.onViewportResize) window.visualViewport?.removeEventListener('resize', this.onViewportResize);
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
      }

      bindEvents() {
        this.querySelectorAll('.sticky-buy-buttons__select').forEach((select) => {
          select.addEventListener('change', this.onOptionChange.bind(this));
        });

        this.chooseButton?.addEventListener('click', this.onChooseOptions.bind(this));

        this.submitButton?.addEventListener('click', () => {
          if (this.submitButton.disabled) return;
          this.setLoading(true);
        });

        this.mainPicker?.addEventListener('change', () => {
          // VariantSelects bails out of renderProductInfo() when the chosen combination
          // maps to no variant, so no variantChange event arrives to update this bar.
          // This listener is registered after the picker's own (it was upgraded first),
          // so currentVariant is already resolved by the time we read it.
          if (!this.mainPicker.currentVariant) this.setUnavailable();
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          this.updateFromSection(event.data.html, event.data.variant);
        });

        // product-form.js drives the add, and its spinner lives on the main button,
        // so the bar clears its own loading state off the cart events.
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.setLoading(false));
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => this.setLoading(false));

        const header = document.querySelector('.section-header');
        if (header) {
          // Height changes (reduce-logo-size, announcement bar closing, resize).
          this.headerResizeObserver = new ResizeObserver(this.updateHeaderOffset.bind(this));
          this.headerResizeObserver.observe(header);

          // sticky-header signals pin/unpin and show/hide purely through class changes,
          // so that is what has to be watched — there is no event to subscribe to.
          this.headerClassObserver = new MutationObserver(this.updateHeaderOffset.bind(this));
          this.headerClassObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
        }

        // The effective position flips at the desktop breakpoint, and that decides
        // whether third-party bottom-corner widgets need pushing up.
        this.breakpoint = window.matchMedia('(min-width: 990px)');
        this.breakpoint.addEventListener('change', this.updateWidgetOffset.bind(this));

        // The bar's own height changes on iOS Safari as the browser chrome shows and
        // hides: env(safe-area-inset-bottom) collapses to 0 once the toolbar covers the
        // home indicator, and expands again when it retracts. Visibility does not change
        // during that, so nothing else here would recompute the published height and the
        // lifted widgets would hold a stale — too tall — offset.
        this.selfResizeObserver = new ResizeObserver(this.updateWidgetOffset.bind(this));
        this.selfResizeObserver.observe(this);

        // Chrome transitions can also move the bar without altering its height.
        this.onViewportResize = this.updateWidgetOffset.bind(this);
        window.visualViewport?.addEventListener('resize', this.onViewportResize);

        // In the theme editor, hold the bar open while the block is selected so the
        // merchant can see what they are configuring.
        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (event) => {
            if (event.target !== this) return;
            this.editorPinned = true;
            this.classList.add('is-visible');
          });

          document.addEventListener('shopify:block:deselect', (event) => {
            if (event.target !== this) return;
            this.editorPinned = false;
            this.updateVisibility();
          });
        }
      }

      /* ----------------------------------------------------------------
         Visibility
         ---------------------------------------------------------------- */

      observeAnchor() {
        const form = document.getElementById(`product-form-${this.sectionId}`);
        // The buttons wrapper rather than the button itself: on back-in-stock products
        // the submit button is `display: none` while disabled, and a hidden element
        // never intersects.
        const anchor =
          form?.querySelector('.product-form__buttons') || document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        if (!anchor) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const scrolledPast = entry.boundingClientRect.bottom < (entry.rootBounds?.top ?? 0);
              this.scrolledPastAnchor = !entry.isIntersecting && scrolledPast;
            });
            this.updateVisibility();
          },
          { threshold: 0 }
        );

        this.observer.observe(anchor);
      }

      observeFooter() {
        const footer = document.querySelector('.footer') || document.querySelector('footer');
        if (!footer) return;

        // Threshold 0 — the bar retreats the moment the first pixel of the footer
        // reaches the bottom of the viewport.
        this.footerObserver = new IntersectionObserver(
          (entries) => {
            this.footerInView = entries.some((entry) => entry.isIntersecting);
            this.updateVisibility();
          },
          { threshold: 0 }
        );

        this.footerObserver.observe(footer);
      }

      updateVisibility() {
        if (!this.editorPinned) {
          this.classList.toggle('is-visible', this.scrolledPastAnchor && !this.footerInView);
        }
        this.updateWidgetOffset();
      }

      /** 'top' or 'bottom' for the viewport width in play right now. */
      effectivePosition() {
        return this.breakpoint?.matches ? this.dataset.desktopPosition : this.dataset.mobilePosition;
      }

      /**
       * Publishes the bar's live height to the document so third-party bottom-corner
       * launchers (Smile.io) can be lifted clear of it — see the stylesheet. Only
       * relevant while the bar is visible AND anchored to the bottom.
       */
      updateWidgetOffset() {
        const root = document.documentElement;
        const isLifting = this.classList.contains('is-visible') && this.effectivePosition() === 'bottom';

        root.classList.toggle('sticky-buy-buttons-lifting-widgets', isLifting);
        root.style.setProperty('--sticky-buy-buttons-height', isLifting ? `${this.offsetHeight}px` : '0px');
      }

      /**
       * A top-anchored bar only needs to clear the header while the header is actually
       * occupying the top of the viewport. sticky-header (sections/header.liquid) tracks
       * that with two classes:
       *
       *   .shopify-section-header-sticky  — pinned to the top right now
       *   .shopify-section-header-hidden  — pinned but translated off-screen
       *
       * So the offset is the header's height only when it is sticky and not hidden. That
       * covers every sticky_header_type: 'none' never gets the sticky class, 'always' and
       * 'reduce-logo-size' get it on load, and 'on-scroll-up' toggles both as you scroll.
       */
      updateHeaderOffset() {
        const header = document.querySelector('.section-header');
        const isPinned =
          header != null &&
          header.classList.contains('shopify-section-header-sticky') &&
          !header.classList.contains('shopify-section-header-hidden');

        this.style.setProperty('--sticky-buy-buttons-offset', isPinned ? `${header.offsetHeight}px` : '0px');
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('loading', isLoading);
        this.spinner?.classList.toggle('hidden', !isLoading);
      }

      /* ----------------------------------------------------------------
         Variant selection
         ---------------------------------------------------------------- */

      onOptionChange(event) {
        if (!this.mainPicker) return;

        const position = parseInt(event.target.dataset.optionPosition, 10);
        const value = event.target.value;

        // variant-selects groups options in `.product-form__input`, variant-radios in `fieldset`.
        const groups = this.mainPicker.querySelectorAll('fieldset, .product-form__input');
        const group = groups[position - 1];
        if (!group) return;

        const select = group.querySelector('select');
        if (select) {
          select.value = value;
        } else {
          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
            (input) => input.value === value
          );
          if (!radio) return;
          radio.checked = true;
        }

        this.withoutGalleryScroll(() => {
          this.mainPicker.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      /**
       * On a stacked gallery, media-gallery.js scrolls the newly active image into
       * view on every variant change. That is fine when the customer is at the top of
       * the page using the main picker, but from the sticky bar it drags them away
       * from the control they just used — so it is suppressed for this one change.
       *
       * Only the media items of this section are neutered (own property shadowing the
       * prototype method), never Element.prototype globally. The gallery queues its
       * scroll during the dispatch, so the restore has to be queued after it.
       */
      withoutGalleryScroll(run) {
        const items = document.querySelectorAll(
          `media-gallery[id^="MediaGallery-${this.sectionId}"] [data-media-id]`
        );

        items.forEach((item) => {
          item.scrollIntoView = () => {};
        });

        run();

        setTimeout(() => {
          items.forEach((item) => {
            delete item.scrollIntoView;
          });
        }, 0);
      }

      onChooseOptions() {
        const target = this.mainPicker || document.getElementById(`ProductInfo-${this.sectionId}`);
        if (!target) return;

        // Reserve the header's full height whenever the header is sticky-capable, rather
        // than whatever it measures right now. This scroll travels upward, which is exactly
        // what makes an 'on-scroll-up' header reveal itself — so by the time the scroll
        // lands, a header that measured 0 on click is back on screen and covering the
        // picker. sticky-header only exists when sticky_header_type is not 'none', and a
        // non-sticky header cannot cover anything, hence the guard.
        const header = document.querySelector('.section-header');
        const headerHeight = header?.querySelector('sticky-header') ? header.offsetHeight : 0;

        // Plus a little breathing room so the picker is not flush against the header.
        const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 24;

        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });

        // Focus the first option once the scroll has settled, so keyboard and screen
        // reader users land on the picker rather than back at the bar.
        setTimeout(() => {
          target.querySelector('select, input[type="radio"]')?.focus({ preventScroll: true });
        }, 500);
      }

      /**
       * Pull this bar's updated markup out of the section render the theme has
       * already fetched for the variant change.
       */
      updateFromSection(html, variant) {
        const price = document.getElementById(`StickyBuyPrice-${this.sectionId}`);
        const priceSource = html.getElementById(`StickyBuyPrice-${this.sectionId}`);
        if (price && priceSource) price.innerHTML = priceSource.innerHTML;

        const image = document.getElementById(`StickyBuyImage-${this.sectionId}`);
        const imageSource = html.getElementById(`StickyBuyImage-${this.sectionId}`);
        if (image && imageSource) image.innerHTML = imageSource.innerHTML;

        const submitSource = html.getElementById(`StickyProductSubmitButton-${this.sectionId}`);
        if (this.submitButton && submitSource) {
          const label = this.submitButton.querySelector('.sticky-buy-buttons__submit-text');
          const labelSource = submitSource.querySelector('.sticky-buy-buttons__submit-text');
          if (label && labelSource) label.textContent = labelSource.textContent;
          this.submitButton.disabled = submitSource.hasAttribute('disabled');
        }

        this.syncOptions(variant);
        this.setLoading(false);
      }

      setUnavailable() {
        if (this.submitButton) {
          const label = this.submitButton.querySelector('.sticky-buy-buttons__submit-text');
          if (label) label.textContent = window.variantStrings?.unavailable || 'Unavailable';
          this.submitButton.disabled = true;
        }

        const price = document.getElementById(`StickyBuyPrice-${this.sectionId}`);
        if (price) price.innerHTML = '';

        this.setLoading(false);
      }

      syncOptions(variant) {
        if (!variant?.options) return;
        this.querySelectorAll('.sticky-buy-buttons__select').forEach((select) => {
          const position = parseInt(select.dataset.optionPosition, 10);
          const value = variant.options[position - 1];
          if (value != null && select.value !== value) select.value = value;
        });
      }
    }
  );
}
